/**
 * `GapAnalysisGuide` — guida contestuale alla colonna "Analisi Gap" del
 * dettaglio professione (`JobDetail`).
 *
 * Rende un piccolo pulsante con l'icona "info" che apre una `Modal` con la
 * legenda dei tre stati della `LevelBar`: verde (requisito soddisfatto), giallo
 * (gap rispetto al Livello richiesto) e rosso (Livello mancante). Per ogni caso
 * mostra un esempio reale della barra, così la legenda ha la **stessa resa**
 * della tabella.
 *
 * Segue lo stesso pattern di `LivelloGuide`: trigger come `<span role="button">`
 * e `Modal` renderizzata in un **portale** con gli eventi fermati, così la guida
 * può stare nell'header di una colonna `DataTable` senza annidare button né far
 * scattare l'ordinamento.
 */
import { useState, type KeyboardEvent, type MouseEvent } from 'react';
import { createPortal } from 'react-dom';
import type { Livello } from '../shared/dto';
import { LevelBar } from './LevelBar';
import { Button, Icon, Modal } from './ui';

export interface GapAnalysisGuideProps {
  /** Classe extra sul trigger (es. per l'allineamento nell'header di colonna). */
  className?: string;
}

/**
 * Livelli attivi (applicabili) negli esempi della legenda: da `e-2` a `e-4`
 * inclusi. Gli altri (`e-1`, `e-5`) restano disattivati nella barra, così la
 * preview mostra anche il trattamento dei livelli non previsti dalla competenza.
 */
const EXAMPLE_LEVELS: Livello[] = ['e-2', 'e-3', 'e-4'];

/** Un caso della legenda: barra d'esempio + titolo + spiegazione. */
interface GapCase {
  /** Livello posseduto nell'esempio (`null` = nessuno). */
  owned: Livello | null;
  /** Livello richiesto dalla professione nell'esempio. */
  required: Livello | null;
  title: string;
  description: string;
}

/**
 * I tre esiti possibili, ordinati dal migliore al peggiore. I valori scelti
 * producono esattamente gli stati `meets` / `gap` / `none` della `LevelBar`.
 */
const GAP_CASES: GapCase[] = [
  {
    owned: 'e-4',
    required: 'e-3',
    title: 'Requisito soddisfatto',
    description:
      'Il livello posseduto raggiunge o supera quello richiesto dalla professione (o la professione non richiede un livello minimo su questa competenza). Nessun gap da colmare.',
  },
  {
    owned: 'e-2',
    required: 'e-4',
    title: 'Gap da colmare',
    description:
      'Il livello posseduto è valido per la competenza ma resta sotto quello richiesto dalla professione: la competenza c’è, ma va portata al livello atteso.',
  },
  {
    owned: null,
    required: 'e-3',
    title: 'Livello mancante',
    description:
      'La competenza non è ancora presidiata: il livello posseduto è "Nessuno" (e-0) o comunque insufficiente a coprire il requisito. È il gap più ampio da colmare.',
  },
];

export function GapAnalysisGuide({ className }: GapAnalysisGuideProps) {
  const [open, setOpen] = useState(false);

  const triggerClasses = ['livello-guide__trigger', className ?? '']
    .filter(Boolean)
    .join(' ');

  const openGuide = (e: MouseEvent | KeyboardEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setOpen(true);
  };
  const onKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') openGuide(e);
  };

  return (
    <>
      <span
        role="button"
        tabIndex={0}
        className={triggerClasses}
        aria-label="Guida all'analisi gap"
        title="Guida all'analisi gap"
        onClick={openGuide}
        onKeyDown={onKeyDown}
      >
        <Icon name="info" size={18} />
      </span>

      {open &&
        createPortal(
          <div
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
          >
            <Modal
              open
              onClose={() => setOpen(false)}
              title="Guida all'analisi gap"
              footer={
                <Button type="button" variant="primary" onClick={() => setOpen(false)}>
                  Ho capito
                </Button>
              }
            >
              <p className="livello-guide__intro">
                Per ogni competenza richiesta dalla professione, la barra confronta
                il <strong>livello posseduto</strong> con il{' '}
                <strong>livello richiesto</strong> e ne evidenzia l’esito con un
                colore. Il marker indica sempre il livello posseduto sulla scala
                <code> e-0..e-5</code>.
              </p>
              <ul className="gap-guide__list">
                {GAP_CASES.map((c) => (
                  <li key={c.title} className="gap-guide__item">
                    <span className="gap-guide__sample">
                      <LevelBar
                        owned={c.owned}
                        required={c.required}
                        available={EXAMPLE_LEVELS}
                      />
                    </span>
                    <span className="gap-guide__text">
                      <span className="gap-guide__title">{c.title}</span>
                      <span className="gap-guide__desc">{c.description}</span>
                    </span>
                  </li>
                ))}
              </ul>
            </Modal>
          </div>,
          document.body,
        )}
    </>
  );
}

export default GapAnalysisGuide;
